import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FileText, Calendar, Download } from "lucide-react";
import { format, parseISO, startOfMonth, subDays, subMonths, subYears, startOfYear, startOfDay, endOfDay, startOfWeek, endOfWeek, previousSunday, nextSaturday } from "date-fns";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import type { AnalysisResult } from "@/types/analysis";
import { Database } from "@/integrations/supabase/types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type CallAnalysis = Database['public']['Tables']['call_analyses']['Row'] & {
  feedback: AnalysisResult['feedback']
};

type DateRange = {
  start: Date;
  end: Date;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = React.useState<DateRange>({
    start: subDays(new Date(), 7),
    end: new Date(),
  });
  const [selectedPreset, setSelectedPreset] = React.useState("last-week");
  const [selectedCategory, setSelectedCategory] = React.useState("Calibrated Questions");
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(undefined);

  const { data: analyses = [], isLoading } = useQuery({
    queryKey: ['call-analyses'],
    queryFn: async () => {
      console.log('Fetching call analyses...');
      const { data, error } = await supabase
        .from('call_analyses')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching analyses:', error);
        throw error;
      }

      const parsedData = data?.map(analysis => ({
        ...analysis,
        feedback: analysis.feedback ? JSON.parse(JSON.stringify(analysis.feedback)) as AnalysisResult['feedback'] : null
      })) || [];

      console.log('Fetched analyses:', parsedData);
      return parsedData as CallAnalysis[];
    },
  });

  const DEFAULT_CATEGORIES = [
    "Rapport Building & Trust",
    "Tactical Empathy",
    "Labeling & Mirroring",
    "Calibrated Questions",
    "No Oriented Questions",
    "Control of Negotiation",
    "Overall Effectiveness"
  ];

  const categories = React.useMemo(() => DEFAULT_CATEGORIES, []);

  React.useEffect(() => {
    if (categories.length > 0 && !categories.includes(selectedCategory)) {
      setSelectedCategory(categories[0]);
    }
  }, [categories, selectedCategory]);

  const handlePresetChange = (preset: string) => {
    setSelectedPreset(preset);
    const now = new Date();
    let start: Date;
    let end: Date = now;

    switch (preset) {
      case 'this-week': {
        start = startOfWeek(now, { weekStartsOn: 0 });
        end = endOfWeek(now, { weekStartsOn: 0 });
        break;
      }
      case 'last-week': {
        const lastSunday = previousSunday(subDays(now, 7));
        start = startOfDay(lastSunday);
        end = endOfDay(nextSaturday(lastSunday));
        break;
      }
      case 'last-month':
        start = subMonths(end, 1);
        break;
      case 'last-3-months':
        start = subMonths(end, 3);
        break;
      case 'last-year':
        start = subYears(end, 1);
        break;
      case 'this-year':
        start = startOfYear(end);
        break;
      default:
        start = subDays(end, 7);
    }

    setDateRange({ start, end });
    setSelectedDate(undefined);
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date) {
      setDateRange({ 
        start: startOfDay(date), 
        end: endOfDay(date) 
      });
      setSelectedPreset('custom');
    }
  };

  const filteredAnalyses = React.useMemo(() => {
    if (!analyses) return [];
    return analyses.filter(analysis => {
      const analysisDate = new Date(analysis.created_at);
      return analysisDate >= dateRange.start && analysisDate <= dateRange.end;
    });
  }, [analyses, dateRange]);

  const prepareBarChartData = (analyses: CallAnalysis[]) => {
    return categories.map(categoryName => ({
      name: categoryName,
      score: analyses.length ? Number((analyses.reduce((acc, analysis) => {
        const categoryData = analysis.feedback?.feedback?.find(
          cat => cat.category === categoryName ||
          (categoryName === "No Oriented Questions" && cat.category === "Implementation of \"No\"-Oriented Questions")
        );
        return acc + (categoryData?.score || 0);
      }, 0) / analyses.length).toFixed(2)) : 0,
    }));
  };

  const prepareLineChartData = (analyses: CallAnalysis[]) => {
    if (!analyses.length) return [];
    
    return analyses
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
      .reduce((acc: any[], analysis) => {
        const date = startOfMonth(parseISO(analysis.created_at));
        const monthKey = format(date, 'MMM yyyy');
        
        const categoryData = analysis.feedback?.feedback?.find(
          cat => cat.category === selectedCategory ||
          (selectedCategory === "No Oriented Questions" && cat.category === "Implementation of \"No\"-Oriented Questions")
        );

        const existingMonth = acc.find(item => item.month === monthKey);
        if (existingMonth) {
          existingMonth.totalScore += categoryData?.score || 0;
          existingMonth.count += 1;
          existingMonth.score = Number((existingMonth.totalScore / existingMonth.count).toFixed(2));
        } else {
          acc.push({
            month: monthKey,
            score: categoryData?.score || 0,
            totalScore: categoryData?.score || 0,
            count: 1,
          });
        }
        return acc;
      }, []);
  };

  const handleDownloadPDF = async (analysis: CallAnalysis) => {
    try {
      const tempDiv = document.createElement('div');
      tempDiv.className = 'p-8 bg-white';
      
      tempDiv.innerHTML = `
        <div style="font-family: Arial, sans-serif;">
          <h1 style="font-size: 24px; color: #1e40af; margin-bottom: 20px;">Sales Call Analysis</h1>
          <div style="margin-bottom: 20px;">
            <p><strong>Date:</strong> ${format(new Date(analysis.created_at), 'MMM d, yyyy h:mm a')}</p>
            <p><strong>Overall Score:</strong> ${analysis.feedback?.summary?.overallScore?.toFixed(2) ?? 'N/A'}/10</p>
          </div>
          
          <div style="margin-bottom: 20px;">
            <h2 style="font-size: 18px; color: #1e40af; margin-bottom: 10px;">What Went Well</h2>
            ${analysis.feedback?.feedback?.map(category => 
              category.whatWentWell?.map(example => 
                `<div style="margin-bottom: 10px;">
                  <p><strong>${category.category}:</strong></p>
                  <p><em>"${example.quote}"</em></p>
                  <p>${example.explanation}</p>
                </div>`
              )?.join('') ?? ''
            ).join('') ?? ''}
          </div>

          <div style="margin-bottom: 20px;">
            <h2 style="font-size: 18px; color: #1e40af; margin-bottom: 10px;">Needs Improvement</h2>
            ${analysis.feedback?.feedback?.map(category => 
              category.needsImprovement?.map(improvement => 
                `<div style="margin-bottom: 10px;">
                  <p><strong>${category.category}:</strong></p>
                  <p><em>Original: "${improvement.originalQuote}"</em></p>
                  <p><strong>Better approach:</strong> "${improvement.betterApproach}"</p>
                  <p>${improvement.explanation}</p>
                </div>`
              )?.join('') ?? ''
            ).join('') ?? ''}
          </div>

          <div style="margin-bottom: 20px;">
            <h2 style="font-size: 18px; color: #1e40af; margin-bottom: 10px;">Next Steps</h2>
            ${analysis.feedback?.summary?.nextSteps?.map(step => 
              `<div style="margin-bottom: 10px;">
                <p><strong>${step.action}</strong></p>
                <p><em>Implementation:</em> ${step.implementation}</p>
                <p><em>Expected Outcome:</em> ${step.expectedOutcome}</p>
              </div>`
            )?.join('') ?? 'No next steps available'}
          </div>
        </div>
      `;

      document.body.appendChild(tempDiv);

      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        logging: false,
        useCORS: true,
      });

      document.body.removeChild(tempDiv);

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        pageNumber++;
      }

      pdf.save(`sales-call-analysis-${format(new Date(analysis.created_at), 'yyyy-MM-dd')}.pdf`);
      toast.success('Analysis downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="w-full p-4 bg-white/50 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Your Call Analysis</h1>
          <div className="flex gap-4">
            <Button onClick={() => navigate('/')} className="bg-primary hover:bg-primary/90">
              New Analysis
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-8">
        {analyses && analyses.length > 0 && (
          <div className="space-y-8">
            <div className="bg-white/50 backdrop-blur-sm rounded-xl border border-gray-100 shadow-sm p-6 space-y-6">
              <div className="flex flex-col md:flex-row justify-between gap-4">
                <h2 className="text-xl font-semibold">Category Scores</h2>
                <div className="flex flex-col md:flex-row gap-4">
                  <Select value={selectedPreset} onValueChange={handlePresetChange}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Select time range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="last-week">Last Week</SelectItem>
                      <SelectItem value="this-week">This Week</SelectItem>
                      <SelectItem value="last-month">Last Month</SelectItem>
                      <SelectItem value="last-3-months">Last 3 Months</SelectItem>
                      <SelectItem value="last-year">Last Year</SelectItem>
                      <SelectItem value="this-year">This Year</SelectItem>
                    </SelectContent>
                  </Select>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-[200px] pl-3 text-left font-normal"
                      >
                        {selectedDate ? (
                          format(selectedDate, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <Calendar className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={selectedDate}
                        onSelect={handleDateSelect}
                        disabled={(date) =>
                          date > new Date() || date < new Date("2023-01-01")
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={prepareBarChartData(filteredAnalyses)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      interval={0}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis domain={[0, 10]} />
                    <Tooltip />
                    <Bar dataKey="score" fill="#1E40AF" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white/50 backdrop-blur-sm rounded-xl border border-gray-100 shadow-sm p-6 space-y-6">
              <div className="flex flex-col md:flex-row justify-between gap-4">
                <h2 className="text-xl font-semibold">Progress Over Time</h2>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-full md:w-[300px]">
                    <SelectValue placeholder="Select category..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={prepareLineChartData(analyses)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="month"
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      interval={0}
                    />
                    <YAxis domain={[0, 10]} />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      stroke="#1E40AF" 
                      strokeWidth={2}
                      dot={{ fill: '#1E40AF' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">Next Steps</h4>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                {filteredAnalyses[0]?.feedback?.summary?.nextSteps?.map((step, index) => (
                  <li key={index}>
                    <strong>{step.action}:</strong> {step.implementation}
                    <br />
                    <span className="text-xs text-gray-500">Expected Outcome: {step.expectedOutcome}</span>
                  </li>
                )) || <li>No next steps available</li>}
              </ul>
            </div>

            <div className="bg-white/50 backdrop-blur-sm rounded-xl border border-gray-100 shadow-sm p-6 space-y-4">
              <h2 className="text-xl font-semibold">Previous Analysis</h2>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Overall Score</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAnalyses.map((analysis) => (
                    <TableRow key={analysis.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          {format(new Date(analysis.created_at), 'MMM d, yyyy h:mm a')}
                        </div>
                      </TableCell>
                      <TableCell>
                        {analysis.feedback?.summary?.overallScore !== undefined
                          ? `${analysis.feedback.summary.overallScore.toFixed(1)} / 10`
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadPDF(analysis)}
                          className="flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Download PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {(!analyses || analyses.length === 0) && (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900">No analyses yet</h3>
            <p className="text-gray-600 mt-2">
              Start by analyzing your first sales call!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
